/**
 * Hook de validações avançadas (avisos e críticos não bloqueantes).
 *
 * Não duplica validação dura de dimensões — isso fica no motor unificado
 * (`validateOrderDimensions` / `useOrderValidations`).
 */

import { useMemo } from 'react';
import { OrderObject } from '../types/objects';
import { debugLog } from '../../../lib/terminalLogger';

type ValidationType = 'info' | 'warning' | 'critical';

export interface AdvancedValidation {
  tipo: ValidationType;
  mensagem: string;
  objetos?: Array<Pick<OrderObject, 'id' | 'nome' | 'width'>>;
}

interface UseAdvancedValidationsProps {
  selectedObjects: OrderObject[];
  metrosVendidos: number;
  totalAreaWithQuantity: number;
  isMetros: boolean;
  /** Quando false (temp/quotation), não emite avisos de metragem vendida vs calculada. */
  compareSoldMeter?: boolean;
  produtoNome?: string;
}

const CANONICAL_WIDTHS_CM: Record<string, number> = {
  'DTF Têxtil': 57.5,
  'DTF UV': 59.0,
  'Sublimação': 157.0,
  'Adesivo': 105.0,
};

const getWidthToleranceCm = (widthCm: number) => (widthCm >= 100 ? 2.0 : 1.0);

export function useAdvancedValidations({
  selectedObjects,
  metrosVendidos,
  totalAreaWithQuantity,
  isMetros,
  compareSoldMeter = true,
  produtoNome,
}: UseAdvancedValidationsProps) {
  const validations: AdvancedValidation[] = useMemo(() => {
    const results: AdvancedValidation[] = [];

    // 1) Metragem menor que o vendido (somente produtos em METROS e fluxos oficiais)
    if (compareSoldMeter && isMetros && metrosVendidos > 0) {
      const diferenca = metrosVendidos - totalAreaWithQuantity;
      const percentualMenor = (diferenca / metrosVendidos) * 100;
      const PERCENTUAL_MINIMO = 10;
      const PERCENTUAL_CRITICO = 20;

      if (diferenca > 0) {
        if (percentualMenor >= PERCENTUAL_CRITICO) {
          results.push({
            tipo: 'critical',
            mensagem: `Metragem calculada (${totalAreaWithQuantity.toFixed(2)}m) é ${percentualMenor.toFixed(1)}% menor que a vendida (${metrosVendidos.toFixed(2)}m).`,
          });
        } else if (percentualMenor >= PERCENTUAL_MINIMO) {
          results.push({
            tipo: 'warning',
            mensagem: `Metragem calculada (${totalAreaWithQuantity.toFixed(2)}m) é ${percentualMenor.toFixed(1)}% menor que a vendida (${metrosVendidos.toFixed(2)}m).`,
          });
        }
      }
    }

    // 2) Similaridade de larguras entre serviços (não bloqueante)
    if (selectedObjects.length > 0) {
      const proximos: Array<Pick<OrderObject, 'id' | 'nome' | 'width'>> = [];
      Object.entries(CANONICAL_WIDTHS_CM).forEach(([, canonical]) => {
        const tol = getWidthToleranceCm(canonical);
        selectedObjects.forEach(o => {
          if (Math.abs(o.width - canonical) <= tol) {
            proximos.push({ id: o.id, nome: o.nome, width: o.width });
          }
        });
      });
      if (proximos.length > 0) {
        results.push({
          tipo: 'warning',
          mensagem: 'Detectamos objetos próximos de larguras típicas de outros serviços. Verifique se o serviço selecionado está correto.',
          objetos: proximos,
        });
      }
    }

    try {
      debugLog('🔍 [ADVANCED_VALIDATIONS] Resumo', {
        tipo: isMetros ? 'METROS' : 'UNIDADES',
        compareSoldMeter,
        totalValidacoes: results.length,
        validacoes: results.map(v => ({
          tipo: v.tipo,
          mensagem: v.mensagem,
          objetosAfetados: v.objetos?.length || 0,
        })),
        metrosVendidos,
        totalAreaWithQuantity,
        produtoNome,
      });
    } catch {}

    return results;
  }, [
    selectedObjects,
    metrosVendidos,
    totalAreaWithQuantity,
    isMetros,
    compareSoldMeter,
    produtoNome,
  ]);

  const hasCritical = validations.some(v => v.tipo === 'critical');

  return {
    validations,
    hasCritical,
    /** @deprecated Dimensões duras são decididas pelo motor unificado (`useOrderValidations`). */
    hasBlocking: false,
  };
}


